import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from "react-redux";

import styles from './UserDetails.module.scss';
import avatar from '../../constants/images/avatar.svg';
import chevron from '../../constants/images/chevron.svg';
import {MAIN_ROUTES} from "../../routing/main_routes";
import {resolveMediaUrl} from "../../configs/urls";
import {persistor} from "../../store/store";

// Inline icons keep the same stroke style as the header's existing SVGs and
// inherit `currentColor`, so each menu item can recolor them via CSS.
const SettingsIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const LogoutIcon = (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.6667 11.3333L14 8M14 8L10.6667 4.66667M14 8H6M6 2H5.2C4.0799 2 3.51984 2 3.09202 2.21799C2.7157 2.40973 2.40973 2.71569 2.21799 3.09202C2 3.51984 2 4.07989 2 5.2V10.8C2 11.9201 2 12.4802 2.21799 12.908C2.40973 13.2843 2.71569 13.5903 3.09202 13.782C3.51984 14 4.0799 14 5.2 14H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const UserDetails = ({handleNavigate, dropUp = false}) => {
    const {user} = useSelector(state => state.userDetails);
    const {cleaner} = useSelector(state => state.cleanerDetails);

    const isCleaner = JSON.parse(localStorage.getItem('isCleaner'));

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const handleLogOutFromAccount = async () => {
        await persistor.purge();
        await localStorage.clear();
        handleNavigate(MAIN_ROUTES.LOGIN);
        window.location.reload();
    }

    // Dropdown sections — grouped arrays render with a thin divider between them.
    // Add new items to a section (or add a whole new section) to extend the menu,
    // e.g. { icon: <ProfileIcon/>, label: 'Профіль', onClick: () => handleNavigate(...) }.
    const menuSections = [
        [
            {icon: SettingsIcon, label: 'Налаштування', onClick: () => handleNavigate(MAIN_ROUTES.SETTINGS)},
            // future account items (профіль, історія замовлень, ...) go here
        ],
        [
            {icon: LogoutIcon, label: 'Вихід', onClick: handleLogOutFromAccount, danger: true},
        ],
    ];

    // close the menu when clicking outside of it
    useEffect(() => {
        if (!isMenuOpen) return;

        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen]);

    const handleItemClick = (onClick) => {
        setIsMenuOpen(false);
        onClick();
    };

    const photoSrc = isCleaner
        ? resolveMediaUrl(cleaner?.user?.photos?.[0]) || avatar
        : resolveMediaUrl(user?.photos?.[0]) || avatar;
    const firstName = isCleaner ? cleaner?.user?.first_name : user?.first_name;
    const lastName = isCleaner ? cleaner?.user?.last_name : user?.last_name;
    const role = isCleaner ? 'Клінер' : 'Замовник';

    return (
        <div className={styles.user_wrapper} ref={menuRef}>
            <div
                className={styles.user_wrapper_user}
                onClick={() => setIsMenuOpen(prev => !prev)}
            >
                <img
                    className={styles.user_wrapper_user_photo}
                    src={photoSrc} alt="user`s photo"
                />
                <p className={styles.user_wrapper_user_fullName}>{firstName} {lastName}</p>
                <img
                    className={`${styles.user_wrapper_user_chevron} ${isMenuOpen ? styles.open : ''}`}
                    src={chevron} alt="chevron"
                />
            </div>

            {isMenuOpen &&
                <div className={`${styles.menu} ${dropUp ? styles.dropUp : ''}`}>
                    <div className={styles.menu_header}>
                        <img className={styles.menu_header_photo} src={photoSrc} alt="avatar"/>
                        <div className={styles.menu_header_info}>
                            <p className={styles.menu_header_info_name}>{firstName} {lastName}</p>
                            <p className={styles.menu_header_info_role}>{role}</p>
                        </div>
                    </div>

                    {menuSections.map((section, sectionIndex) => (
                        <React.Fragment key={sectionIndex}>
                            <div className={styles.menu_divider}/>
                            <ul className={styles.menu_list}>
                                {section.map(({icon, label, onClick, danger}) => (
                                    <li
                                        key={label}
                                        className={`${styles.menu_list_item} ${danger ? styles.danger : ''}`}
                                        onClick={() => handleItemClick(onClick)}
                                    >
                                        <span className={styles.menu_list_item_icon}>{icon}</span>
                                        <span className={styles.menu_list_item_label}>{label}</span>
                                    </li>
                                ))}
                            </ul>
                        </React.Fragment>
                    ))}
                </div>}
        </div>
    );
};

export default UserDetails;
